import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtErr, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { markReadingCompleted } from "../components/ReviewPrompt";
import { toast } from "sonner";
import { notifyQuotaExhausted } from "../lib/quota";
import { ReadingActions } from "../components/ReadingActions";
import { MarkdownText } from "../components/MarkdownText";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { DeleteReadingButton } from "../components/DeleteReadingButton";
import { MoonStar, LayoutGrid, Check } from "lucide-react";

const GeoFigure = ({ fig, large = false }) => (
  <div className="flex flex-col items-center gap-1.5" data-testid={`geo-figure-${fig.role.toLowerCase().replace(/\s+/g, "-")}`}>
    <svg viewBox="0 0 44 62" className={large ? "w-16 h-[88px]" : "w-11 h-16"} data-testid={`geo-dots-${fig.role.toLowerCase().replace(/\s+/g, "-")}`}>
      {fig.pattern.map((v, i) =>
        v === 1 ? (
          <circle key={i} cx="22" cy={9 + i * 15} r="3.6" fill="#d4af37" />
        ) : (
          <g key={i}>
            <circle cx="13" cy={9 + i * 15} r="3.6" fill="#d4af37" />
            <circle cx="31" cy={9 + i * 15} r="3.6" fill="#d4af37" />
          </g>
        )
      )}
    </svg>
    <p className="text-[10px] font-cinzel tracking-[0.12em] uppercase text-purple-300/50 text-center">{fig.role}</p>
    <p className={`font-display ${large ? "text-2xl" : "text-sm"} text-amber-100 text-center leading-tight`}>{fig.name}</p>
  </div>
);

export default function Geomanzia() {
  const { user, loading } = useAuth();
  const [question, setQuestion] = useState("");
  const [session, setSession] = useState(null);
  const [marks, setMarks] = useState([]);
  const [starting, setStarting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [geoText, setGeoText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [params] = useSearchParams();
  const squareRef = useRef(null);

  const saveSnapshot = () => {
    try {
      if (session?.id) sessionStorage.setItem("arcana-resume-geomanzia", JSON.stringify({ id: session.id }));
    } catch {}
  };

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/geomancy/sessions", { question });
      setSession(data);
      setMarks([]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "quota_esaurita") {
        notifyQuotaExhausted();
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setStarting(false);
    }
  };

  const handleSquareClick = (e) => {
    if (!session || session.status !== "active") return;
    const rect = squareRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMarks((m) => [...m, { x, y }]);
  };

  const finishRow = async () => {
    if (marks.length <= 8 || finishing) return;
    setFinishing(true);
    try {
      const { data } = await api.post(`/geomancy/sessions/${session.id}/rows`, { clicks: marks.length });
      setSession(data);
      setMarks([]);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setFinishing(false);
    }
  };

  const streamInterpretation = async (sessionId) => {
    setStreaming(true);
    setGeoText("");
    try {
      const res = await fetch(`${API}/geomancy/sessions/${sessionId}/interpret`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Interpretazione non disponibile");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta") setGeoText((t) => t + ev.content);
          else if (ev.type === "error") throw new Error(ev.content);
        }
      }
      markReadingCompleted("geomanzia");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStreaming(false);
    }
  };

  useEffect(() => {
    let sid = params.get("sessione");
    if (!sid) {
      try {
        const raw = sessionStorage.getItem("arcana-resume-geomanzia");
        if (raw) {
          sessionStorage.removeItem("arcana-resume-geomanzia");
          sid = JSON.parse(raw)?.id || null;
        }
      } catch {}
    }
    if (sid) {
      api.get(`/geomancy/sessions/${sid}`)
        .then((r) => {
          setSession(r.data);
          if (r.data.interpretation) setGeoText(r.data.interpretation);
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (session?.status === "completed" && session?.figures && !session?.interpretation && !geoText && !streaming) {
      streamInterpretation(session.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  const reset = () => {
    setSession(null);
    setQuestion("");
    setMarks([]);
    setGeoText("");
    setStreaming(false);
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="geomanzia-login-required">
        <div className="mystic-card p-10">
          <MoonStar className="w-10 h-10 text-amber-400/60 mx-auto mb-5" />
          <h1 className="font-display text-3xl text-amber-100 mb-4">Geomanzia</h1>
          <p className="text-purple-200/70 text-sm mb-8">
            Per generare i tuoi segni geomantici è necessario accedere o creare un account: le tue
            generazioni resteranno custodite nella tua Area Cliente.
          </p>
          <Link to="/accedi?redirect=/geomanzia" data-testid="geomanzia-login-btn" className="gold-btn inline-block px-8 py-3 text-sm">
            Accedi o registrati
          </Link>
        </div>
      </div>
    );
  }

  const currentRow = session ? session.rows.length + 1 : 1;
  const completed = session?.status === "completed";
  const geoStatus = session?.figures?.status || "ok";
  const geoBlocked = completed && !["ok", "rebuilt_angular"].includes(geoStatus);

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16 sm:py-20" data-testid="geomanzia-page">
      <div className="text-center mb-12">
        <p className="eyebrow">Metodo interattivo</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 mt-4">Geomanzia</h1>
        <p className="text-purple-200/70 mt-4 text-sm max-w-xl mx-auto">
          Poni la tua domanda, poi tocca il quadrato: ogni tocco genera un segno della tua sequenza geomantica.
        </p>
      </div>

      {!session && (
        <div className="mystic-card p-7 sm:p-9 space-y-6" data-testid="geomanzia-form">
          <div className="rounded-lg bg-[#0f0617] border border-amber-500/20 p-5 text-sm text-purple-100/80 leading-relaxed space-y-3" data-testid="geomanzia-instructions">
            <p>Pensa alla domanda che vuoi porre alla Geomanzia e concentrati su di essa.</p>
            <p>Scrivi la tua domanda nello spazio dedicato.</p>
            <p>Quando sei pronto, clicca liberamente all'interno del quadrato, senza contare i clic e senza pensare a quanti ne stai facendo.</p>
            <p>Continua a cliccare finché senti di aver terminato questa prima sequenza.</p>
            <p>Quando hai finito, premi “Ho finito questa riga”.</p>
            <p>Il sistema passerà automaticamente alla riga successiva.</p>
            <p>Ripeti lo stesso procedimento fino a completare tutte le 16 righe.</p>
            <p className="text-amber-200/90">Non devi contare i clic: pensa solamente alla tua domanda e lasciati guidare dall'istinto.</p>
          </div>
          <div>
            <p className="eyebrow mb-4">La tua domanda</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Scrivi qui la tua domanda…"
              data-testid="geomanzia-question-input"
              className="input-dark min-h-[110px] resize-y"
            />
          </div>
          <button
            onClick={start}
            disabled={starting || question.trim().length < 5}
            data-testid="geomanzia-start-btn"
            className="gold-btn w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <LayoutGrid className="w-4 h-4" /> {starting ? "Preparazione…" : "Inizia la generazione"}
          </button>
        </div>
      )}

      {session && (
        <div className="space-y-8">
          <div className="mystic-card p-7 sm:p-9" data-testid="geomanzia-board">
            <p className="text-sm text-purple-300/60 italic mb-6">“{session.question}”</p>

            {!completed && (
              <>
                <div className="text-center mb-6" data-testid="geomanzia-progress">
                  <p className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400">
                    Riga {currentRow} di {session.total_rows}
                  </p>
                  <p className="text-xs text-purple-300/60 mt-2">
                    Clicca liberamente nel quadrato, senza contare. Quando senti di aver terminato, premi il pulsante.
                  </p>
                </div>

                <div
                  ref={squareRef}
                  onClick={handleSquareClick}
                  data-testid="geomanzia-square"
                  className="relative w-full aspect-square max-w-md mx-auto rounded-xl border-2 border-amber-500/40 bg-[#0f0617] cursor-pointer select-none overflow-hidden hover:border-amber-400/70 transition-colors"
                >
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {marks.length === 0 && (
                      <p className="text-purple-300/40 text-sm text-center px-6">Tocca il quadrato, lasciati guidare dall'istinto</p>
                    )}
                  </div>
                  {marks.map((m, i) => (
                    <span
                      key={i}
                      className="absolute w-2 h-2 rounded-full bg-amber-400 fade-up"
                      style={{ left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%, -50%)" }}
                    />
                  ))}
                </div>

                <button
                  onClick={finishRow}
                  disabled={marks.length <= 8 || finishing}
                  data-testid="geomanzia-finish-row-btn"
                  className="gold-btn w-full max-w-md mx-auto mt-6 py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check className="w-4 h-4" />
                  {finishing ? "Salvataggio…" : currentRow >= session.total_rows ? "Ho finito l'ultima riga" : "Ho finito questa riga"}
                </button>
              </>
            )}

            {session.rows.length > 0 && (
              <div className={`space-y-2.5 ${!completed ? "mt-8 pt-6 border-t border-amber-500/15" : ""}`} data-testid="geomanzia-rows">
                {session.rows.map((r) => (
                  <div
                    key={r.index}
                    className="flex items-center justify-between rounded-lg bg-[#180a26] border border-[#4a2175]/60 px-4 py-3"
                    data-testid={`geomanzia-row-${r.index}`}
                  >
                    <p className="text-sm text-amber-200 font-cinzel tracking-[0.15em] uppercase">Riga {r.index}</p>
                    <p className="text-sm text-emerald-300/80 flex items-center gap-1.5">
                      <Check className="w-4 h-4" /> Completata
                    </p>
                  </div>
                ))}
              </div>
            )}

            {completed && session.figures && (
              <div className="pt-2 space-y-9" data-testid="geomanzia-complete">
                <p className="font-display text-2xl text-amber-100 text-center">Il tuo scudo geomantico</p>
                {session.figures.status === "rebuilt_angular" && (
                  <p className="text-center text-amber-200/75 text-xs italic" data-testid="geo-rebuilt-note">
                    Il Giudice iniziale era una figura muta: lo scudo è stato ricostruito dalle case angolari, come previsto dal metodo.
                  </p>
                )}

                {[
                  ["Le Madri", session.figures.mothers, "madri"],
                  ["Le Figlie", session.figures.daughters, "figlie"],
                  ["Le Nipoti", session.figures.nieces, "nipoti"],
                ].map(([label, figs, key]) => (
                  <div key={key} data-testid={`geo-group-${key}`}>
                    <p className="eyebrow text-center mb-4">{label}</p>
                    <div className="grid grid-cols-4 gap-2">
                      {figs.map((f) => <GeoFigure key={f.role} fig={f} />)}
                    </div>
                  </div>
                ))}

                <div data-testid="geo-group-testimoni">
                  <p className="eyebrow text-center mb-4">I Testimoni</p>
                  <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto">
                    {session.figures.witnesses.map((f) => <GeoFigure key={f.role} fig={f} />)}
                  </div>
                </div>

                <div data-testid="geo-group-giudice">
                  <p className="eyebrow text-center mb-4">Il Giudice</p>
                  <div className="flex justify-center">
                    <GeoFigure fig={session.figures.judge} large />
                  </div>
                </div>

                {session.figures.supreme_judge && (
                  <div data-testid="geo-group-giudice-supremo">
                    <p className="eyebrow text-center mb-4">Il Giudice Supremo</p>
                    <div className="flex justify-center">
                      <GeoFigure fig={session.figures.supreme_judge} />
                    </div>
                  </div>
                )}

                {!geoBlocked && (session.figures.parte_fortuna || session.figures.parte_spirito) && (
                  <div data-testid="geo-group-parti">
                    <p className="eyebrow text-center mb-4">Le Parti</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                      {session.figures.parte_fortuna && (
                        <div className="rounded-lg bg-[#180a26] border border-[#4a2175]/60 px-4 py-4 text-center" data-testid="geo-parte-fortuna">
                          <p className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-purple-300/50 mb-1.5">Parte di Fortuna</p>
                          <p className="font-display text-lg text-amber-100 leading-tight">{session.figures.parte_fortuna.figura}</p>
                          <p className="text-xs text-purple-200/60 mt-1">in {session.figures.parte_fortuna.casa}ª Casa</p>
                        </div>
                      )}
                      {session.figures.parte_spirito && (
                        <div className="rounded-lg bg-[#180a26] border border-[#4a2175]/60 px-4 py-4 text-center" data-testid="geo-parte-spirito">
                          <p className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-purple-300/50 mb-1.5">Parte dello Spirito</p>
                          <p className="font-display text-lg text-amber-100 leading-tight">{session.figures.parte_spirito.figura}</p>
                          <p className="text-xs text-purple-200/60 mt-1">in {session.figures.parte_spirito.casa}ª Casa</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!geoBlocked && session.figures.via_punti?.length > 0 && (
                  <div data-testid="geo-group-via-punti">
                    <p className="eyebrow text-center mb-4">La Via dei Punti</p>
                    <div className="space-y-2 max-w-lg mx-auto">
                      {session.figures.via_punti.map((v, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-3 rounded-lg bg-[#180a26] border border-[#4a2175]/60 px-4 py-2.5"
                          data-testid={`geo-via-punti-${i}`}
                        >
                          <p className="text-xs text-purple-200/70">Linea {v.elemento}</p>
                          <p className="text-sm text-amber-100 font-display text-right">{v.figura} · {v.casa}ª Casa</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {geoBlocked && (
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 text-center" data-testid="geo-blocked-notice">
                    <p className="font-cinzel text-xs tracking-[0.2em] uppercase text-amber-400 mb-3">Il tema non può essere interpretato</p>
                    <div className="text-purple-100/85 text-sm leading-relaxed" data-testid="geomanzia-blocked-message">
                      <MarkdownText text={geoText} />
                      {streaming && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
                    </div>
                  </div>
                )}

                {!geoBlocked && (
                <div className="pt-6 border-t border-amber-500/15">
                  <p className="eyebrow mb-4 text-center">L'interpretazione</p>
                  <div className="text-purple-100/85 leading-loose text-sm sm:text-base whitespace-pre-line" data-testid="geomanzia-interpretation">
                    <MarkdownText text={geoText} />
                    {streaming && <span className="inline-block w-2 h-4 bg-amber-400/70 ml-1 animate-pulse" />}
                  </div>
                  {!streaming && geoText && (
                    <div className="mt-4 flex justify-center gap-2 flex-wrap">
                      <CopyReadingButton text={geoText} testid="copy-reading-geomanzia" />
                      {session?.id && (
                        <DeleteReadingButton testid="delete-reading-geomanzia" onDelete={async () => {
                          await api.delete(`/geomancy/sessions/${session.id}`);
                          toast.success("Testo eliminato.");
                          reset();
                        }} />
                      )}
                    </div>
                  )}
                  {streaming && !geoText && (
                    <p className="text-purple-300/50 text-sm text-center">Arcana sta interpretando lo scudo…</p>
                  )}
                  {!streaming && geoText && (
                    <p className="text-xs text-purple-300/50 text-center mt-5 leading-relaxed">
                      La lettura ha finalità di riflessione e orientamento personale. Eventuali tempistiche
                      indicate sono puramente indicative e non costituiscono una certezza.
                    </p>
                  )}
                </div>
                )}
              </div>
            )}
            {completed && (
              <ReadingActions
                readingId={!streaming && geoText ? session.id : null}
                assistantTo={`/assistente?tipo=geomanzia&id=${session.id}`}
                onReset={reset}
                onAssistant={saveSnapshot}
                resetLabel="Nuova generazione"
                testid="geomanzia"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
